import dayjs from 'dayjs';
import * as R from 'ramda';
import { BranchConstants, DIET_BRIEF_ANSWERS, DIET_TARGET_ANSWERS, SURVEY_STATUS_CODES, VALID_SURVEY_MONTH_LIMIT, getSurveyIdList } from 'src/constants';
import { SurveyCategory, SurveyType } from 'src/types';
import { isChildClinic } from './client';
export var getSurveysByIncludedQuestionIDs = function getSurveysByIncludedQuestionIDs(questionIDs) {
  return R.pipe(R.pathOr([], ['survey_answers']), R.find(function (answer) {
    return questionIDs.includes(answer.question_id);
  }));
};
export var getBriefInfoFromSurvey = function getBriefInfoFromSurvey(surveys, questionIDs) {
  var splitter = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ', ';

  if (!surveys) {
    return R.mapObjIndexed(function () {
      return '';
    }, questionIDs);
  }

  return R.mapObjIndexed(function (_ref) {
    var questionIDs = _ref.questionIDs;
    var answer = R.pipe(R.filter(R.pipe(getSurveysByIncludedQuestionIDs(questionIDs), R.isNotNil)), R.sort(function (a, b) {
      var _b$taken_at;

      return b === null || b === void 0 ? void 0 : (_b$taken_at = b.taken_at) === null || _b$taken_at === void 0 ? void 0 : _b$taken_at.localeCompare(a === null || a === void 0 ? void 0 : a.taken_at);
    }), R.head, getSurveysByIncludedQuestionIDs(questionIDs), R.propOr('', 'answer'), R.when(R.is(Array), R.pipe(R.map(R.prop('answer')), R.join(splitter))))(surveys);
    return answer;
  })(questionIDs);
};
export var getDietBriefFromSurvey = function getDietBriefFromSurvey(surveys, splitter) {
  return getBriefInfoFromSurvey(surveys, DIET_BRIEF_ANSWERS, splitter);
};
export var getTargetBriefFromSurvey = function getTargetBriefFromSurvey(surveys, splitter) {
  return getBriefInfoFromSurvey(surveys, DIET_TARGET_ANSWERS, splitter);
};
export var getSurveyStatus = function getSurveyStatus(surveys, isChildClinic) {
  var filteredSurveys = R.filter(function (survey) {
    var taken_at = dayjs(survey.taken_at);
    var months = dayjs().diff(taken_at, 'month', true);
    return months <= VALID_SURVEY_MONTH_LIMIT;
  }, surveys);
  var ids = R.pipe(R.filter(R.propEq(false, 'is_draft')), R.map(R.prop('survey_form_id')))(filteredSurveys);
  var draftIds = R.pipe(R.filter(R.propEq(true, 'is_draft')), R.map(R.prop('survey_form_id')))(filteredSurveys);
  var initSurveyIds = getSurveyIdList(SurveyType.INIT_SURVEY_ID, BranchConstants.ORG_TYPE, isChildClinic ? SurveyCategory.CHILD : SurveyCategory.ADULT);
  var hormonesSurveyIds = getSurveyIdList(SurveyType.HORMONES_SURVEY_ID, BranchConstants.ORG_TYPE, isChildClinic ? SurveyCategory.CHILD : SurveyCategory.ADULT);
  var completedInitSurvey = R.intersection(ids)(initSurveyIds).length > 0;
  var completedHormonesSurvey = R.intersection(ids)(hormonesSurveyIds).length > 0;
  var isDraftInitSurvey = R.intersection(draftIds)(initSurveyIds).length > 0;
  var isDraftHormonesSurvey = R.intersection(draftIds)(hormonesSurveyIds).length > 0;

  var getStatus = function getStatus(statusCode) {
    return isChildClinic ? "".concat(statusCode, "_child") : statusCode;
  };

  if (completedInitSurvey && completedHormonesSurvey) {
    // init:completed, hormones:completed
    return SURVEY_STATUS_CODES.filled_out_completely;
  } else if (isDraftInitSurvey && isDraftHormonesSurvey) {
    // init:draft, hormones:draft
    return getStatus(SURVEY_STATUS_CODES.not_completed);
  } else if (isDraftInitSurvey) {
    // init:draft, hormones:completed or not filled out
    return getStatus(completedHormonesSurvey ? SURVEY_STATUS_CODES.not_completed_basic_questionnaire : SURVEY_STATUS_CODES.not_completed);
  } else if (isDraftHormonesSurvey) {
    // init:completed or not filled out, hormones:draft
    return getStatus(completedInitSurvey ? SURVEY_STATUS_CODES.not_completed_hormone_questionnaire : SURVEY_STATUS_CODES.not_completed);
  } else if (completedInitSurvey) {
    // init:completed, hormones:not filled out
    return getStatus(SURVEY_STATUS_CODES.not_filled_out_hormone_questionnaire);
  } else if (completedHormonesSurvey) {
    // init:not filled out, hormones:completed
    return getStatus(SURVEY_STATUS_CODES.not_filled_out_basic_questionnaire);
  } // init:not filled out, hormones:not filled out


  return getStatus(SURVEY_STATUS_CODES.not_filled_out);
};
export var getSurveyStatusFromVisit = function getSurveyStatusFromVisit(visit) {
  var surveys = R.pathOr([], ['client', 'surveys'], visit);
  return getSurveyStatus(surveys, isChildClinic(visit === null || visit === void 0 ? void 0 : visit.service_name));
};
export var getSurveyStatusColor = function getSurveyStatusColor(status) {
  var color = status !== SURVEY_STATUS_CODES.filled_out_completely ? 'red.400' : '';
  return color;
};
export var mapSurveyFormIdsFromSurveyTypes = function mapSurveyFormIdsFromSurveyTypes(surveyTypes) {
  var surveyTypeIdMap = Object.fromEntries(surveyTypes.map(function (type) {
    return [type, getSurveyIdList(type, BranchConstants.ORG_TYPE)];
  }));
  var surveyFormIds = Object.values(surveyTypeIdMap).flat();
  return surveyFormIds;
};